import React from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';

/**
 * SEOMetaTags Component
 * 
 * This component allows for page-specific SEO meta tags to be set.
 * It uses react-helmet to inject meta tags into the document head.
 * 
 * @param {Object} props
 * @param {string} props.title - The page title
 * @param {string} props.description - The page description
 * @param {string} props.keywords - Comma-separated keywords
 * @param {string} props.canonicalUrl - The canonical URL for the page
 * @param {string} props.ogImage - The Open Graph image URL
 * @param {string} props.alternateUrl - The URL of an alternate version of this page (if applicable)
 * @param {boolean} props.isRedirectTarget - Whether this page is a target of a redirect
 */
const SEOMetaTags = ({ 
  title = "NM Diamond | Premium Diamond Jewelry Wholesaler in Delhi",
  description = "NM Diamond is Delhi's premier diamond jewelry wholesaler offering exquisite rings, necklaces, earrings, and bracelets at competitive prices. Visit our Karol Bagh showroom for exclusive collections and custom designs.",
  keywords = "nmdiamond, NM Diamond, Karol Bagh diamond wholesale, diamond jewelry Delhi",
  canonicalUrl = null,
  ogImage = "media/og-image.jpg",
  alternateUrl = null,
  isRedirectTarget = false
}) => {
  // Construct the full title with brand name
  const fullTitle = title.includes("NM Diamond") ? title : `${title} | NM Diamond`;
  const location = useLocation();
  
  // If no canonical URL is specified, construct it from the current path
  const baseUrl = "https://www.nmdiamond.in"; // Always include www for consistency
  const currentPath = location.pathname;
  
  // Handle canonicalUrl parameter to ensure consistent format
  let effectiveCanonicalUrl;
  if (canonicalUrl) {
    // If canonical URL is provided, ensure it has www
    if (canonicalUrl.includes('://nmdiamond.in')) {
      effectiveCanonicalUrl = canonicalUrl.replace('://nmdiamond.in', '://www.nmdiamond.in');
    } else {
      effectiveCanonicalUrl = canonicalUrl;
    }
  } else {
    // Otherwise construct from current path
    effectiveCanonicalUrl = `${baseUrl}${currentPath}`;
  }
  
  // Handle alternateUrl to ensure consistent format
  let effectiveAlternateUrl = null;
  if (alternateUrl) {
    if (alternateUrl.includes('://nmdiamond.in')) {
      effectiveAlternateUrl = alternateUrl.replace('://nmdiamond.in', '://www.nmdiamond.in');
    } else {
      effectiveAlternateUrl = alternateUrl;
    }
  }
  
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      
      {/* Canonical URL - critical for SEO */}
      <link rel="canonical" href={effectiveCanonicalUrl} />
      
      {/* Alternate URL if this page has alternate versions */}
      {effectiveAlternateUrl && (
        <link rel="alternate" href={effectiveAlternateUrl} />
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={effectiveCanonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage.startsWith('http') ? ogImage : `${baseUrl}/${ogImage}`} />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={effectiveCanonicalUrl} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={ogImage.startsWith('http') ? ogImage : `${baseUrl}/${ogImage}`} />
    </Helmet>
  );
};

export default SEOMetaTags; 